// VoiceRecorderButton (web) — click-to-toggle mic for the DM composer.
//
// Click mic → record begins; click again (or click ✕) → stop. Cancel
// discards; stop runs presign → PUT → sendMessage. Hard-caps at 60s.
//
// Codec: tries `audio/webm; codecs=opus` first (Chrome / Firefox /
// Edge), falls back to `audio/mp4` on Safari. The declared upload
// mime is `audio/webm` or `audio/mp4` — both are IANA-registered and
// the browser dispatches its decoder on them. (We do NOT relabel as
// `audio/m4a`, which is non-standard and prevents Chrome/Firefox
// from decoding the bytes when they're served back from R2.)

import { useCallback, useEffect, useRef, useState } from 'react';

import { Icons } from './Icons';

// Backend cap — mirrors MAX_DM_VOICE_DURATION_MS in dms.ts.
export const MAX_VOICE_DURATION_MS = 60_000;

type RecorderStatus = 'idle' | 'recording' | 'uploading';

type RecordedClip = {
  blob: Blob;
  mimeType: string;       // declared mime for upload (audio/webm | audio/mp4)
  durationMs: number;
};

export function VoiceRecorderButton({
  onUpload,
  onSend,
  onRecordingChange,
  disabled = false,
}: {
  /** Presign + PUT the recorded clip. Throws on failure. */
  onUpload: (input: {
    kind: 'voice';
    file: Blob;
    mimeType: string;
    sizeBytes: number;
    durationMs: number;
  }) => Promise<{
    storageKey: string;
    mimeType: string;
    sizeBytes: number;
    durationMs: number;
  }>;
  /** Fire sendMessage with the finalized voice attachment. */
  onSend: (finalized: {
    storageKey: string;
    mimeType: string;
    sizeBytes: number;
    durationMs: number;
  }) => Promise<void> | void;
  /** Lets the parent collapse other composer children while recording. */
  onRecordingChange?: (recording: boolean) => void;
  disabled?: boolean;
}) {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The `onstop` handler needs to know whether to upload or discard.
  // Refs (not state) because the handler is registered once and fires
  // synchronously from MediaRecorder's internal queue.
  const cancelRef = useRef(false);
  // Mime we'll declare to the backend. Set when we successfully open
  // the recorder; mirrors the actual chunk container.
  const declaredMimeRef = useRef('audio/webm');

  const setStatusBoth = useCallback(
    (next: RecorderStatus) => {
      setStatus(next);
      // Hold the row through `uploading` too — the recording pill
      // stays visible while the PUT is in flight.
      onRecordingChange?.(next !== 'idle');
    },
    [onRecordingChange],
  );

  const clearTimers = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const finalizeAndSend = useCallback(
    async (clip: RecordedClip) => {
      setStatusBoth('uploading');
      try {
        if (clip.blob.size <= 0) {
          throw new Error('empty-recording');
        }
        const finalized = await onUpload({
          kind: 'voice',
          file: clip.blob,
          mimeType: clip.mimeType,
          sizeBytes: clip.blob.size,
          durationMs: clip.durationMs,
        });
        await onSend(finalized);
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert("Couldn't send the voice note. Try again.");
        if (import.meta.env.DEV) {
          console.warn('[VoiceRecorderButton] send failed', e);
        }
      } finally {
        setStatusBoth('idle');
        setElapsedMs(0);
      }
    },
    [onSend, onUpload, setStatusBoth],
  );

  const startRecording = useCallback(async () => {
    if (status !== 'idle' || disabled) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Microphone access denied. Enable it in your browser settings to send voice notes.');
      return;
    }
    streamRef.current = stream;

    // Codec selection — webm/opus is the universal default; Safari only
    // supports audio/mp4 (AAC). We declare the canonical IANA mime
    // (`audio/mp4`, RFC 4337) on upload — NOT `audio/m4a`, which is
    // non-standard and breaks Chrome/Firefox playback when the server
    // serves the bytes back under it.
    let mr: MediaRecorder | null = null;
    let blobMime = 'audio/webm';      // what MediaRecorder produces
    let uploadMime = 'audio/webm';    // what we declare on upload
    const tryCreate = (mimeType: string, declared: string): boolean => {
      try {
        if (
          'isTypeSupported' in MediaRecorder &&
          !MediaRecorder.isTypeSupported(mimeType)
        ) {
          return false;
        }
        mr = new MediaRecorder(stream, { mimeType });
        blobMime = mimeType;
        uploadMime = declared;
        return true;
      } catch {
        return false;
      }
    };
    if (
      !tryCreate('audio/webm;codecs=opus', 'audio/webm') &&
      !tryCreate('audio/webm', 'audio/webm') &&
      !tryCreate('audio/mp4;codecs=mp4a.40.2', 'audio/mp4') &&
      !tryCreate('audio/mp4', 'audio/mp4')
    ) {
      releaseStream();
      // eslint-disable-next-line no-alert
      window.alert("Your browser doesn't support voice recording in this app.");
      return;
    }
    declaredMimeRef.current = uploadMime;

    chunksRef.current = [];
    cancelRef.current = false;
    const recorder = mr!;
    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: blobMime });
      const durMs = Math.min(
        MAX_VOICE_DURATION_MS,
        Date.now() - startedAtRef.current,
      );
      releaseStream();
      recorderRef.current = null;
      if (cancelRef.current || durMs < 500) {
        // <500ms clips are accidental; discard without burning a
        // presign round-trip.
        setStatusBoth('idle');
        setElapsedMs(0);
        return;
      }
      void finalizeAndSend({
        blob,
        mimeType: declaredMimeRef.current,
        durationMs: durMs,
      });
    };
    recorderRef.current = recorder;
    try {
      recorder.start();
    } catch {
      releaseStream();
      recorderRef.current = null;
      // eslint-disable-next-line no-alert
      window.alert("Couldn't start the recorder.");
      return;
    }
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setStatusBoth('recording');

    tickRef.current = setInterval(() => {
      setElapsedMs(
        Math.min(MAX_VOICE_DURATION_MS, Date.now() - startedAtRef.current),
      );
    }, 100);
    stopTimerRef.current = setTimeout(() => {
      stopRecording();
    }, MAX_VOICE_DURATION_MS);
  }, [disabled, finalizeAndSend, setStatusBoth, status]);

  const stopRecording = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const cancelRecording = useCallback(() => {
    cancelRef.current = true;
    stopRecording();
  }, [stopRecording]);

  // Unmount safety — kill the recorder + free the stream so a tab
  // close after starting a record doesn't leave the mic indicator on.
  useEffect(() => {
    return () => {
      clearTimers();
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      releaseStream();
    };
  }, []);

  if (status === 'idle') {
    return (
      <button
        type="button"
        onClick={() => void startRecording()}
        disabled={disabled}
        aria-label="Record voice note"
        title="Record voice note"
        style={iconBtn}
      >
        <MicIcon />
      </button>
    );
  }

  return (
    <div style={pill} role="group" aria-label="Recording voice note">
      <span style={dot} />
      <span style={time}>{formatTime(elapsedMs)}</span>
      <span style={hint}>
        {status === 'uploading' ? 'Sending…' : 'Recording — tap ■ to send'}
      </span>
      <button
        type="button"
        onClick={cancelRecording}
        disabled={status === 'uploading'}
        aria-label="Cancel recording"
        title="Cancel"
        style={{ ...pillCancelBtn, opacity: status === 'uploading' ? 0.5 : 1 }}
      >
        ✕
      </button>
      <button
        type="button"
        onClick={() => stopRecording()}
        disabled={status === 'uploading'}
        aria-label="Stop and send recording"
        title="Stop and send"
        style={{ ...pillStopBtn, opacity: status === 'uploading' ? 0.5 : 1 }}
      >
        <StopIcon />
      </button>
    </div>
  );
}

function MicIcon() {
  return <Icons.mic size={18} />;
}

function StopIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" fill="currentColor" />
    </svg>
  );
}

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Inline styles — keeps the bundle CSS-import-free and matches the
// other DMThread.tsx primitives.
const iconBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 18,
  border: 'none',
  background: 'transparent',
  color: 'var(--coral)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const pill: React.CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 10px 6px 14px',
  borderRadius: 18,
  background: 'var(--chip)',
  minHeight: 36,
};

const dot: React.CSSProperties = {
  width: 9,
  height: 9,
  borderRadius: 5,
  background: 'var(--coral)',
  flexShrink: 0,
  animation: 'vrb-pulse 1.2s ease-in-out infinite',
};

const time: React.CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink)',
  fontVariantNumeric: 'tabular-nums',
};

const hint: React.CSSProperties = {
  flex: 1,
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  color: 'var(--ink-3)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const pillCancelBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 14,
  border: 'none',
  background: 'transparent',
  color: 'var(--ink-3)',
  cursor: 'pointer',
  fontSize: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const pillStopBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  border: 'none',
  background: 'var(--coral)',
  color: '#fff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Pulse keyframe registered once per page mount via a singleton style
// tag — the recorder usually only renders within a DM thread so a
// scoped style is overkill.
if (typeof document !== 'undefined' && !document.getElementById('vrb-styles')) {
  const el = document.createElement('style');
  el.id = 'vrb-styles';
  el.textContent = `
    @keyframes vrb-pulse {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.4; }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-vrb-dot] { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
